import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Answer.css';

interface AnswerData {
  title: string;
  content: string[];
  steps?: string[];
  disclaimer?: string;
}

const Answer: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get the FAQ type from URL params
  const faqType = searchParams.get('type') || 'unsubscribe';

  // Mock data - in a real app, this would come from an API
  const getAnswerData = (type: string): AnswerData => {
    switch (type) {
      case 'unsubscribe':
        return {
          title: t('answer.unsubscribe.title'),
          content: t('answer.unsubscribe.content', { returnObjects: true }) as string[],
          steps: t('answer.unsubscribe.steps', { returnObjects: true }) as string[],
          disclaimer: t('answer.unsubscribe.disclaimer')
        };
      case 'refund':
        return {
          title: t('answer.refund.title'),
          content: t('answer.refund.content', { returnObjects: true }) as string[],
          steps: t('answer.refund.steps', { returnObjects: true }) as string[],
          disclaimer: t('answer.refund.disclaimer')
        };
      case 'login':
        return {
          title: t('answer.login.title'),
          content: t('answer.login.content', { returnObjects: true }) as string[],
          steps: t('answer.login.steps', { returnObjects: true }) as string[],
          disclaimer: t('answer.login.disclaimer')
        };
      default:
        return {
          title: 'Help Information',
          content: ['Please select a specific help topic from the help page.']
        };
    }
  };

  const answerData = getAnswerData(faqType);

  const handleSupportContactClick = () => {
    navigate('/help-contact');
  };

  const renderStepText = (step: string, index: number) => {
    // Check if this is the refund step with support contact
    if (faqType === 'refund' && index === 0 && step.includes('support@aicourses.me')) {
      const parts = step.split('support@aicourses.me');
      return (
        <span className="step-text">
          {parts[0]}
          <span 
            className="support-contact-link" 
            onClick={handleSupportContactClick}
            style={{ 
              color: '#007bff', 
              cursor: 'pointer', 
              textDecoration: 'underline' 
            }}
          >
            support@aicourses.me
          </span>
          {parts[1]}
        </span>
      );
    }
    return <span className="step-text">{step}</span>;
  };

  return (
    <div className="answer-page">
      <div className="answer-container">
        <div className="answer-content">
          <div className="answer-title">{answerData.title}</div>
          {answerData.content.map((paragraph, index) => (
            <p key={index} className="answer-paragraph">
              {paragraph}
            </p>
          ))}

          {answerData.steps && (
            <div className="answer-steps">
              {answerData.steps.map((step, index) => (
                <div key={index} className="step-item">
                  <span className="step-number">{index + 1}.</span>
                  {renderStepText(step, index)}
                </div>
              ))}
            </div>
          )}

          {answerData.disclaimer && (
            <p className="answer-disclaimer">
              {answerData.disclaimer}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Answer;
